import { all, put, call, getContext, takeLatest } from 'redux-saga/effects';

import { Logger } from 'utils/Logger';

import {
  GET_DOCUMENT_LINK,
  REQUEST_FUNDING_SEARCH_DOCUMENT_LINK,
  REQUEST_FUNDING_SEARCH_DOCUMENTS_LINK,
} from './types';

import {
  fundingDocumentUploadStart,
  fundingDocumentUploadSuccess,
  fundingDocumentUploadFailure,
  fundingDocumentUploadProgress,
} from './reducer';

import { uploadFileWithProgressSaga } from '../utils/FileUploader';

const logger = new Logger('Saga:Documents');

export function* requestUploadFundingDocumentSaga(
  identifier,
  fundingSearchId,
  documentType,
  files,
) {
  const routerService = yield getContext('routerService');

  // Trigger upload start
  yield put(fundingDocumentUploadStart(identifier, fundingSearchId, documentType, files));

  try {
    // Build form data
    const data = new FormData();
    data.append('documentTypeId', documentType);
    files.forEach((file, index) => data.append(`files[${index}]`, file));

    const response = yield call(
      uploadFileWithProgressSaga,
      routerService.generate('api.funding_search_upload', { fundingSearchId }),
      data,
      fundingDocumentUploadProgress,
      [identifier],
    );

    yield put(fundingDocumentUploadSuccess(identifier, response.data.documents));

    return response;
  } catch (error) {
    logger.error('Failed to upload documents with the following error', error);
    yield put(fundingDocumentUploadFailure(identifier, error));

    throw error;
  }
}

export function* requestRemoveFundingDocumentSaga(fundingSearchId, documentId) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  return yield call(
    authApiService.delete,
    routerService.generate('api.funding_search_remove', {
      fundingSearchId,
      documentId,
    }),
  );
}

export function* requestFundingDocumentLinkSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.get,
      routerService.generate('api.funding_search_download', {
        fundingSearchId: action.fundingSearchId,
        documentId: action.documentId,
      }),
    );

    action.promise.resolve(response.data.url);
  } catch (error) {
    logger.error(
      'Failed to request link to funding search document with the following error',
      error,
    );
    action.promise.reject(error);
  }
}

export function* requestFundingDocumentsLinkSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.get,
      routerService.generate('api.download_funding_search_documents', {
        fundingSearchId: action.fundingSearchId,
      }),
    );

    action.promise.resolve(response.data.url);
  } catch (error) {
    logger.error(
      'Failed to request link to funding search documents with the following error',
      error,
    );
    action.promise.reject(error);
  }
}

export function* requestDocumentLinkSaga({ route, promise }) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');
  const { endpoint, interpolations } = route;

  if (!endpoint) {
    promise.reject('No endpoint provided');
  }

  try {
    const response = yield call(
      authApiService.get,
      routerService.generate(endpoint, interpolations),
      { responseType: 'blob' },
    );

    promise.resolve(response.data);
  } catch (error) {
    logger.error('Failed to get link for document with the following error', error);

    promise.reject(error);
  }
}

export default function* documentsSaga() {
  return yield all([
    takeLatest(GET_DOCUMENT_LINK, requestDocumentLinkSaga),
    takeLatest(REQUEST_FUNDING_SEARCH_DOCUMENT_LINK, requestFundingDocumentLinkSaga),
    takeLatest(REQUEST_FUNDING_SEARCH_DOCUMENTS_LINK, requestFundingDocumentsLinkSaga),
  ]);
}
